import type { Question, Dependency, Answer } from '../types/api';

/**
 * SIMPLIFIED DEPENDENCY EVALUATION FOR STANDARD QUESTIONNAIRES
 * 
 * New approach: When a question's answer changes, check if it has a dependency_list.
 * If yes, directly update only the exclusion states of questions in that dependency_list.
 * No need to search through all questions - much more efficient!
 */

/**
 * Evaluates a single dependency condition with direct value comparison
 * @param dependency - The dependency configuration with requiredValue as {typedValue, selectedOptionId}
 * @param controllingAnswer - The answer from the controlling question
 * @returns Whether the dependent question should be shown
 */
export const evaluateDependency = (
  dependency: Dependency,
  controllingAnswer: Answer | undefined
): boolean => {
  // If no answer exists for the controlling question, dependency is not met
  if (!controllingAnswer || !controllingAnswer.value) {
    return false;
  }

  const { value } = controllingAnswer;
  const { requiredValue, conditionType } = dependency;

  // Handle different condition types
  switch (conditionType) {
    case 'equals': {
      // For text inputs, directly compare typedValue
      const answerTypedValue = value.typedValue || '';
      const requiredTypedValue = requiredValue.typedValue || '';
      
      return answerTypedValue.toLowerCase().trim() === requiredTypedValue.toLowerCase().trim();
    }

    case 'option_text_equals': {
      // For radio/dropdown selections, directly compare selectedOptionId
      const answerOptionId = value.selectedOptionId;
      const requiredOptionId = requiredValue.selectedOptionId;
      
      if (!answerOptionId || !requiredOptionId) {
        return false;
      }
      
      return String(answerOptionId) === String(requiredOptionId);
    }

    default: {
      console.warn(`Unknown condition type: ${conditionType}`);
      return false;
    }
  }
};

/**
 * Recursively finds a question by ID in the question tree (including subQuestions)
 * @param questions - Array of questions to search in
 * @param questionId - The ID to search for
 * @returns The found question or undefined
 */
export const findQuestionById = (
  questions: Question[],
  questionId: string
): Question | undefined => {
  for (const question of questions) {
    if (question.id === questionId) {
      return question;
    }

    if (question.subQuestions && question.subQuestions.length > 0) {
      const found = findQuestionById(question.subQuestions, questionId);
      if (found) return found;
    }
  }
  return undefined;
};

/**
 * Updates the exclusion state of specific questions based on a controlling question's dependency_list
 * This is called when a question's answer changes and it has a dependency_list
 * @param controllingQuestion - The question whose answer changed
 * @param controllingAnswer - The new answer for the controlling question
 * @param allQuestions - All questions for finding dependent questions
 * @returns Array of updated questions (only the ones that were in dependency_list)
 */
export const updateDependentQuestionsExclusionState = (
  controllingQuestion: Question,
  controllingAnswer: Answer | undefined,
  allQuestions: Question[]
): Question[] => {
  // If no dependency_list, nothing to update
  if (!controllingQuestion.dependency_list || controllingQuestion.dependency_list.length === 0) {
    return [];
  }

  const updatedQuestions: Question[] = [];

  // Process each dependency in the list
  controllingQuestion.dependency_list.forEach(dependency => {
    const dependentQuestion = findQuestionById(
      allQuestions,
      dependency.questionId.toString()
    );

    if (!dependentQuestion) {
      return; // Skip if question not found
    }

    // Evaluate if dependent question should be shown
    const shouldShow = evaluateDependency(dependency, controllingAnswer);

    // Create updated question with new exclusion state
    const updatedQuestion: Question = {
      ...dependentQuestion,
      excluded: !shouldShow,
      exclusionReason: shouldShow ? undefined : `Dependency condition not met for controlling question ${controllingQuestion.id}`,
    };

    // Simple: SubQuestions inherit parent's exclusion state
    if (updatedQuestion.subQuestions && updatedQuestion.subQuestions.length > 0) {
      updatedQuestion.subQuestions = updatedQuestion.subQuestions.map(subQ => ({
        ...subQ,
        excluded: updatedQuestion.excluded,
        exclusionReason: updatedQuestion.excluded ? 'Parent question excluded due to dependency' : undefined,
      }));
    }

    updatedQuestions.push(updatedQuestion);
  });

  return updatedQuestions;
};

/**
 * Extracts all questions from a nested structure (including subQuestions) into a flat array
 * @param questions - Array of questions (may contain subQuestions)
 * @returns Flat array of all questions
 */
export const flattenQuestions = (questions: Question[]): Question[] => {
  const result: Question[] = [];

  for (const question of questions) {
    result.push(question);

    if (question.subQuestions && question.subQuestions.length > 0) {
      result.push(...flattenQuestions(question.subQuestions));
    }
  }

  return result;
};

/**
 * Synchronous cleanup - removes answers for questions that are excluded
 * @param answers - Current answers object
 * @param excludedQuestionIds - Array of question IDs that are now excluded
 * @returns Cleaned answers object
 */
export const cleanupExcludedAnswersSync = (
  answers: Record<string, Answer>,
  excludedQuestionIds: string[]
): Record<string, Answer> => {
  const cleanedAnswers = { ...answers };

  excludedQuestionIds.forEach(questionId => {
    if (cleanedAnswers[questionId]) {
      console.log(`🧹 Removing answer for excluded question: ${questionId}`);
      delete cleanedAnswers[questionId];
    }
  });

  return cleanedAnswers;
};

/**
 * Synchronous version - removes answers for questions that are excluded (no attachment clearing)
 * Used in reducers where async operations are not allowed
 * @param answers - Current answers object
 * @param questions - Array of questions to check
 * @returns Cleaned answers object with excluded questions' answers removed
 */
export const cleanupExcludedQuestionAnswersSync = (
  answers: Record<string, Answer>,
  questions: Question[]
): Record<string, Answer> => {
  const cleanedAnswers = { ...answers };
  const flatQuestions = flattenQuestions(questions);

  // Remove answers for any excluded questions
  flatQuestions.forEach(question => {
    if (question.excluded && cleanedAnswers[question.id]) {
      console.log(`🧹 Removing answer for excluded question: ${question.id}`);
      delete cleanedAnswers[question.id];
    }
  });

  return cleanedAnswers;
};

/**
 * Async version - removes answers for questions that are excluded AND clears their attachments
 * Used in services during save operations where async operations are allowed
 * @param hotelId - Hotel ID
 * @param answers - Current answers object
 * @param questions - Array of questions to check
 * @param updateQuestionUploadedFilenames - Function to update uploaded filenames through reducer
 * @returns Promise resolving to cleaned answers object
 */
export const cleanupExcludedQuestionAnswersWithAttachments = async (
  hotelId: string,
  answers: Record<string, Answer>,
  questions: Question[],
  updateQuestionUploadedFilenames: (questionId: string, uploadedFilenames: string[]) => void
): Promise<{ cleanedAnswers: Record<string, Answer> }> => {
  const cleanedAnswers = { ...answers };
  const flatQuestions = flattenQuestions(questions);

  // Collect excluded questions with answers
  const excludedQuestionsWithAnswers = flatQuestions.filter(
    question => question.excluded && cleanedAnswers[question.id]
  );

  if (excludedQuestionsWithAnswers.length > 0) {
    console.log(`🧹 Found ${excludedQuestionsWithAnswers.length} excluded questions to clean up:`, 
      excludedQuestionsWithAnswers.map(q => q.id));

    // Clear attachments for excluded questions (also clears uploaded_filenames via reducer)
    try {
      const { clearAttachmentsForExcludedQuestions } = await import('./attachmentDeletionUtils');
      await clearAttachmentsForExcludedQuestions(hotelId, excludedQuestionsWithAnswers, updateQuestionUploadedFilenames);
    } catch (error) {
      console.error('Failed to clear attachments for excluded questions:', error);
      // Continue with answer cleanup even if attachment clearing fails
    }

    // Remove answers for excluded questions
    excludedQuestionsWithAnswers.forEach(question => {
      console.log(`🧹 Removing answer for excluded question: ${question.id}`);
      delete cleanedAnswers[question.id];
    });
  }

  // Note: uploaded_filenames are already cleared by clearAttachmentsForExcludedQuestions via reducer
  // No need to manually clear them here anymore

  return { cleanedAnswers };
};
